import { TouchEvent, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { voicePartForRange, voicePartName } from '../../../../common/src/learn_rules.ts';
import { churchOrgTypes, User } from '../../../../common/src/model.ts';
import { getLearnPath, LearnPages, Pages } from '../../../../common/src/pages.ts';
import { naturalStringCompare } from '../../../../common/src/util.ts';
import ForwardArrowIcon from '../../assets/arrow-next-right-icon.svg?react';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { synchronizeOrgsWithServer, useCurrentOrg } from '../../data/use_current_org.tsx';
import {
  deleteHouseholdLocalStorageEntries,
  synchronizeHouseholdWithServer,
  useHousehold,
} from '../../data/use_household.tsx';
import { useLearnProgress } from '../../data/use_learn_progress.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { useUser } from '../../data/use_user.tsx';
import { useUserAttributes } from '../../data/use_user_attributes.ts';
import { manageSubscription, useContainingAppVersion } from '../../util/billing.ts';
import { confirm } from '../../util/confirm.tsx';
import { containingAppPlatformLabel } from '../../util/containing_app.ts';
import { getUrlForPage } from '../../util/path.ts';
import { getSubscriptionStatus, SubscriptionAction } from '../../util/subscription_status.ts';
import { learnEntryState } from '../learn/learn_navigation.tsx';
import { SongOptions } from '../settings/song_options.tsx';
import { userInitial } from './profile_avatar.tsx';
import { closeProfileDrawer, notifyProfileDrawerNavigation, useProfileDrawerOpen } from './profile_drawer_state.ts';

const swipeCloseThresholdPx = 40;

export const vocalRangeRowTestId = 'profile-vocal-range-row';
export const vocalRangeNotSet = 'Not set';

// Left drawer over the current screen with the most-used settings inline (issue #3356).
// Mounted once in Root; opened by the header ProfileAvatar via the shared drawer state.
export const ProfileDrawer = () => {
  const isOpen = useProfileDrawerOpen();
  const navigate = useNavigate();
  const location = useLocation();
  const { household } = useHousehold();
  const { user } = useUser();
  const [activeUserToken, setActiveUserToken] = useLocalStorage(LocalStorageKey.ActiveUserToken);
  const { householdOrgs } = useCurrentOrg();
  const { isInternalUser } = useUserAttributes();
  const { progress: learnProgress } = useLearnProgress();
  const containingAppVersion = useContainingAppVersion();
  const touchStartXRef = useRef<number | undefined>(undefined);

  // The old settings page force-synced household + orgs on mount; the drawer replaced it,
  // so refresh both whenever it opens (e.g. a trial expiring or subscription changing
  // propagates without a page reload)
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void synchronizeHouseholdWithServer({ force: true });
    void synchronizeOrgsWithServer({ force: true });
  }, [isOpen]);

  // Any navigation closes the drawer, except when a reopen was scheduled (the /settings
  // redirect); see profile_drawer_state.ts. Keyed off location.key changes rather than a
  // first-run boolean so a StrictMode double-invoked mount effect cannot consume the reopen flag.
  const lastLocationKeyRef = useRef(location.key);
  useEffect(() => {
    if (lastLocationKeyRef.current === location.key) {
      return;
    }
    lastLocationKeyRef.current = location.key;
    notifyProfileDrawerNavigation();
  }, [location.key]);

  const onTouchStart = useCallback((event: TouchEvent) => {
    touchStartXRef.current = event.touches[0]?.clientX;
  }, []);

  const onTouchMove = useCallback((event: TouchEvent) => {
    const startX = touchStartXRef.current;
    const currentX = event.touches[0]?.clientX;
    if (startX === undefined || currentX === undefined) {
      return;
    }
    if (startX - currentX > swipeCloseThresholdPx) {
      touchStartXRef.current = undefined;
      closeProfileDrawer();
    }
  }, []);

  const openDialogPage = useCallback(
    async (page: Pages) => {
      await navigate(getUrlForPage(page));
    },
    [navigate],
  );

  const onSubscription = useCallback(
    async (action: SubscriptionAction) => {
      closeProfileDrawer();
      if (action === 'manage') {
        manageSubscription();
        return;
      }
      // Both 'subscribe' and 'chooseTrialOrSubscribe' land on the subscription gate
      // (Pages.SubscriptionTrial): it offers the free trial only pre-trial and is
      // subscribe-only during/after the trial. The onboarding subscribe island is
      // avoided here because it redirects comped households to the download page.
      await navigate(getUrlForPage(Pages.SubscriptionTrial));
    },
    [navigate],
  );

  const onAddMember = useCallback(async () => {
    await navigate(getUrlForPage(Pages.AddMember));
  }, [navigate]);

  // "My vocal range" opens the Range Finder (issue #3481); redoing it overwrites the saved range. The
  // Range Finder's way back returns to the tab the drawer was opened on (learn_navigation.tsx).
  const onVocalRange = useCallback(async () => {
    await navigate(getLearnPath(LearnPages.Range), { state: learnEntryState(location.pathname) });
  }, [navigate, location.pathname]);

  const onSignOut = useCallback(async () => {
    if (
      await confirm({
        confirmation: (
          <span>
            Are you sure you wish to
            <br />
            Sign Out?
          </span>
        ),
      })
    ) {
      deleteHouseholdLocalStorageEntries();
      window.location.href = '/';
    }
  }, []);

  const church = householdOrgs.filter(({ type }) => churchOrgTypes.includes(type))[0];
  const subscriptionStatus = getSubscriptionStatus({ household, org: church });
  const subtitle = [household?.familyLastName && `${household.familyLastName} household`, church?.name]
    .filter(Boolean)
    .join(' · ');
  const members = [...(household?.users ?? [])].sort((lhs, rhs) => naturalStringCompare(lhs.name, rhs.name));

  return (
    <>
      <Scrim $show={isOpen} onClick={closeProfileDrawer} />
      <DrawerPanel $show={isOpen} onTouchStart={onTouchStart} onTouchMove={onTouchMove}>
        {/* fits without scrolling at rest; scrolls only while an accordion is expanded */}
        <ScrollableContent>
          <Identity>
            <BigAvatar>{userInitial(user?.name)}</BigAvatar>
            <div style={{ overflow: 'hidden' }}>
              <IdentityName>{user?.name}</IdentityName>
              {subtitle && <IdentitySubtitle>{subtitle}</IdentitySubtitle>}
            </div>
          </Identity>

          <SectionHeading>Household members · tap to switch</SectionHeading>
          <MembersRow>
            {members.map((member) => (
              <MemberEntry
                key={member.token}
                member={member}
                isActive={member.token === activeUserToken}
                onSelect={setActiveUserToken}
              />
            ))}
            <Member onClick={() => void onAddMember()}>
              <AddAvatar>+</AddAvatar>
              <MemberName>Add</MemberName>
            </Member>
          </MembersRow>

          <SectionHeading $tightBottom>Song options</SectionHeading>
          <SongOptionsWrapper>
            <SongOptions />
          </SongOptionsWrapper>

          <Divider />
          <LinkRow onClick={() => void openDialogPage(Pages.Household)}>
            <span>Household</span>
            <RowValue>
              Name, email, church
              <Chevron />
            </RowValue>
          </LinkRow>
          <LinkRow
            $inert={subscriptionStatus.action === 'none'}
            onClick={
              subscriptionStatus.action === 'none' ? undefined : () => void onSubscription(subscriptionStatus.action)
            }
          >
            <span>Subscription</span>
            <RowValue>
              {subscriptionStatus.label}
              {subscriptionStatus.action !== 'none' && <Chevron />}
            </RowValue>
          </LinkRow>
          {isInternalUser && (
            // Learn is internal-only for now, and so is this row (issue #3481)
            <LinkRow data-testid={vocalRangeRowTestId} onClick={() => void onVocalRange()}>
              <span>My vocal range</span>
              <RowValue>
                {learnProgress.range ? (
                  <RowValueLarge>
                    {voicePartName(learnProgress.voicePart ?? voicePartForRange(learnProgress.range))}
                  </RowValueLarge>
                ) : (
                  vocalRangeNotSet
                )}
                <Chevron />
              </RowValue>
            </LinkRow>
          )}
          <LinkRow onClick={() => void openDialogPage(Pages.Tutorials)}>
            <span>Tutorials</span>
            <RowValue>
              <Chevron />
            </RowValue>
          </LinkRow>
          <LinkRow onClick={() => void openDialogPage(Pages.Help)}>
            <span>Help</span>
            <RowValue>
              <Chevron />
            </RowValue>
          </LinkRow>
        </ScrollableContent>

        <DrawerBottom>
          <SignOut onClick={() => void onSignOut()}>Sign out</SignOut>
          {containingAppVersion && (
            <Version>
              {containingAppPlatformLabel(containingAppVersion.platform)} {containingAppVersion.version}
            </Version>
          )}
        </DrawerBottom>
      </DrawerPanel>
    </>
  );
};

function MemberEntry({
  member,
  isActive,
  onSelect,
}: {
  member: User;
  isActive: boolean;
  onSelect: (token: string) => void;
}) {
  const onClick = useCallback(() => {
    onSelect(member.token);
  }, [member.token, onSelect]);
  const firstName = member.name.trim().split(/\s+/)[0];
  return (
    <Member onClick={onClick}>
      <MemberAvatar $active={isActive}>{userInitial(member.name)}</MemberAvatar>
      <MemberName $active={isActive}>{firstName}</MemberName>
    </Member>
  );
}

const Scrim = styled.div<{ $show: boolean }>`
  z-index: ${({ $show }) => ($show ? '300' : '-20')};
  position: fixed;
  inset: 0;
  ${({ $show }) =>
    $show &&
    `
    background-color: var(--color-background-modal-overlay);
    transition: background-color 200ms ease-in-out;
  `};
`;

const DrawerPanel = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: 85vw;
  max-width: 360px;
  z-index: 301;
  display: flex;
  flex-direction: column;
  padding: 16px 16px 14px;
  background-color: var(--color-background);
  color: var(--color-text);
  box-shadow: ${({ $show }) => ($show ? '4px 0 24px rgba(0, 0, 0, 0.25)' : 'none')};
  transform: translateX(${({ $show }) => ($show ? '0' : '-105%')});
  transition: transform 250ms ease-in-out;
  overflow: hidden;
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  /* SongOptions carries hidden nowrap content (e.g. Report an Issue) wider than the
     drawer; never allow a horizontal scrollbar regardless of what it renders */
  overflow-x: hidden;
  /* iOS overlay scrollbars sit on the flush-right chevrons/toggles on small screens */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: none;
`;

const BigAvatar = styled.div`
  flex: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--color-nightingale);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 24px;
`;

const IdentityName = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 19px;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const IdentitySubtitle = styled.div`
  font-size: 13px;
  color: var(--color-text-darker);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

// $tightBottom: the song-option rows carry their own internal padding (40px centered
// rows contribute ~10px above the label text), so that heading needs less margin than
// the members row for the two visual gaps to match
const SectionHeading = styled.div<{ $tightBottom?: boolean }>`
  flex: none;
  margin: 22px 0 ${({ $tightBottom }) => ($tightBottom ? '0' : '12px')};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 11.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-section-heading);
  text-align: left;
`;

// The active ring (2px outline + 2px offset) extends 4px past the avatar; padding gives
// it room inside this scroll container while the negative margin keeps the row aligned.
const MembersRow = styled.div`
  display: flex;
  gap: 12px;
  flex: none;
  overflow-x: auto;
  padding: 4px;
  margin: -4px;
`;

const Member = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  width: 48px;
  flex: none;
  cursor: pointer;
`;

const MemberAvatar = styled.div<{ $active: boolean }>`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  ${({ $active }) =>
    $active
      ? `
    background-color: var(--color-nightingale);
    color: white;
    outline: 2px solid var(--color-nightingale);
    outline-offset: 2px;
  `
      : `
    background-color: var(--color-background-selected-item);
    color: var(--color-nightingale);
  `};
`;

const MemberName = styled.div<{ $active?: boolean }>`
  max-width: 48px;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $active }) => ($active ? 'var(--color-text)' : 'var(--color-text-darker)')};
  ${({ $active }) => $active && 'font-family: Jost-SemiBold, Arial, sans-serif;'};
`;

const AddAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed var(--color-text-lighter);
  color: var(--color-text-darker);
  font-size: 22px;
`;

// SongOptions carries a bottom margin for its settings page and player kebab hosts;
// the drawer supplies its own vertical rhythm instead. The gray .setting hairlines
// are dropped entirely in the drawer — the gold Divider below is the only separator.
const SongOptionsWrapper = styled.div`
  flex: none;

  & > div {
    margin-bottom: 0 !important;
  }

  & .setting {
    border-bottom: none;
  }

  /* rows sit flush with the section headings and gold divider (16px content edges):
     no label indent, and the chevron/toggle ends at the divider's right edge */
  & .setting .settingName {
    padding-left: 0;
  }

  & .setting > svg {
    margin-right: 0;
  }

  & .toggle {
    width: 50px;
  }
`;

const Divider = styled.div`
  flex: none;
  border-top: 1px solid var(--color-hairline);
  margin: 8px 0 2px;
`;

const LinkRow = styled.div<{ $inert?: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;
  font-size: 16px;
  cursor: ${({ $inert }) => ($inert ? 'default' : 'pointer')};
`;

const RowValue = styled.div`
  display: flex;
  align-items: center;
  color: var(--color-text-darker);
  font-size: 15px;
  white-space: nowrap;
`;

// The part ("Bass") at the row's right, in the larger type the label uses
const RowValueLarge = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 16px;
  color: var(--color-text);
`;

// Same square box and 6px gap as the accordion chevron above the divider so the
// value text columns right-align across both row types.
const Chevron = styled(ForwardArrowIcon)`
  flex: none;
  width: 12px;
  height: 12px;
  margin-left: 6px;
  fill: var(--color-text-darker);
`;

// No horizontal padding: Sign out left-aligns with the section headings and the
// version label right-aligns with the chevrons (the panel's 16px content edges)
const DrawerBottom = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0 0;
  flex: none;
`;

const SignOut = styled.div`
  color: var(--color-dangerous-button);
  font-size: 16px;
  cursor: pointer;
`;

const Version = styled.div`
  color: var(--color-text-lighter);
  font-size: 12.5px;
`;
